import React from 'react';
import type { InvoiceHeader } from '../services/invoiceApi';
import { Branch } from '../services/branchApi';
import { Customer } from '../services/customerApi';
import { formatDate } from '../utils/dateUtils';
import { getInvoiceExportCountryDisplayLine, isInvoiceExportSupplyType } from '../utils/invoiceExportCountry';

interface MetaField {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}

function MetaCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="invoice-meta-cell">
      <span className="invoice-meta-label">{label}</span>
      <span className="invoice-meta-value">{value ?? ''}</span>
    </div>
  );
}

export interface InvoiceAddressAndMetaProps {
  invoice: InvoiceHeader;
  branchDetails: Branch | null;
  shipToCustomer: Customer | null;
  billToCustomer: Customer | null;
  exportBranchLutDisplay?: string;
  exportBranchLutLoading?: boolean;
}

export const InvoiceAddressAndMeta: React.FC<InvoiceAddressAndMetaProps> = ({
  invoice,
  branchDetails,
  shipToCustomer,
  billToCustomer,
  exportBranchLutDisplay = '',
  exportBranchLutLoading = false,
}) => {
  const isExport = isInvoiceExportSupplyType(invoice);

  const metaFields: MetaField[] = [
    { label: 'Invoice No.', value: invoice.invoiceNo ?? '' },
    { label: 'Dated', value: invoice.invoiceDate ? formatDate(invoice.invoiceDate) : '' },
    { label: 'e-Way Bill No.', value: invoice.ewayBillNo ?? '' },
    { label: 'Delivery Note', value: invoice.deliverNote ?? '' },
    { label: 'Reference No. & Date', value: invoice.referenceNoAndDate ?? '' },
    { label: 'Other References', value: invoice.otherReferences ?? '' },
    { label: "Buyer's Order No.", value: invoice.buyerOrderNo ?? '' },
    {
      label: "Buyer's Order Dated",
      value: invoice.buyerOrderDate ? formatDate(invoice.buyerOrderDate) : '',
    },
    { label: 'Dispatch Doc No.', value: invoice.dispatchDocNo ?? '' },
    {
      label: 'Delivery Note Date',
      value: invoice.deliverNoteDate ? formatDate(invoice.deliverNoteDate) : '',
    },
    {
      label: 'Dispatched through',
      value:
        String(
          invoice.ewayTransporterName ??
            (invoice as { eway_transporter_name?: string }).eway_transporter_name ??
            ''
        ).trim() ||
        (invoice.dispatchedThrough ?? ''),
    },
    { label: 'Destination', value: invoice.destination ?? '' },
  ];

  if (isExport) {
    metaFields.push(
      { label: 'Country', value: getInvoiceExportCountryDisplayLine(invoice) },
      {
        label: 'LUT',
        value: exportBranchLutLoading ? '…' : exportBranchLutDisplay || '—',
      }
    );
  }

  metaFields.push(
    { label: 'Bill of Lading / LR-RR No.', value: invoice.billOfLanding ?? '' },
    { label: 'Motor Vehicle No.', value: invoice.motorVehicleNo ?? '' },
    { label: 'Terms of Delivery', value: invoice.termOfDelivery ?? '', fullWidth: true }
  );

  return (
    <div className="invoice-address-meta-wrap">
      <div className="invoice-address-col">
        <div className="invoice-address-section">
          <div className="flex gap-3 items-start">
            {branchDetails ? (
              <>
                <div className="shrink-0">
                  <img
                    src="/customer-logo/sg-group-logo.jpg"
                    alt="SG Group"
                    className="h-16 w-[5.5rem] object-contain"
                  />
                </div>
                <div className="min-w-0">
                  <p className="invoice-party-name">{branchDetails.branchName}</p>
                  <p className="invoice-party-line">{branchDetails.address}</p>
                  <p className="invoice-party-line">GSTIN/UIN: {branchDetails.gstin || 'NA'}</p>
                  <p className="invoice-party-line">
                    State Name: {branchDetails.stateName || 'NA'}, Code: {branchDetails.stateCode || 'NA'}
                  </p>
                </div>
              </>
            ) : null}
          </div>
        </div>

        <div className="invoice-address-section">
          <p className="invoice-section-heading">Consignee (Ship to):</p>
          {shipToCustomer ? (
            <div>
              <p className="invoice-party-name">{shipToCustomer.customerName}</p>
              <p className="invoice-party-line">{shipToCustomer.address}</p>
              <p className="invoice-party-line">Vendor Code: {shipToCustomer.vendorCode ?? 'NA'}</p>
              <p className="invoice-party-line">GSTIN/UIN: {shipToCustomer.gstNo ?? 'NA'}</p>
              <p className="invoice-party-line">
                State Name: {shipToCustomer.stateName ?? 'NA'}, Code: {shipToCustomer.stateCode ?? 'NA'}
              </p>
            </div>
          ) : (
            <p className="invoice-party-line">—</p>
          )}
        </div>

        <div className="invoice-address-section">
          <p className="invoice-section-heading">Buyer (Bill to):</p>
          {billToCustomer ? (
            <div>
              <p className="invoice-party-name">{billToCustomer.customerName}</p>
              <p className="invoice-party-line">{billToCustomer.address}</p>
              <p className="invoice-party-line">Vendor Code: {billToCustomer.vendorCode ?? 'NA'}</p>
              <p className="invoice-party-line">GSTIN/UIN: {billToCustomer.gstNo ?? 'NA'}</p>
              <p className="invoice-party-line">
                State Name: {billToCustomer.stateName ?? 'NA'}, Code: {billToCustomer.stateCode ?? 'NA'}
              </p>
            </div>
          ) : (
            <p className="invoice-party-line">—</p>
          )}
        </div>
      </div>

      <div className="invoice-meta-grid min-w-0">
        {metaFields.map((field) =>
          field.fullWidth ? (
            <div key={field.label} className="invoice-meta-cell invoice-meta-cell-full">
              <span className="invoice-meta-label">{field.label}</span>
              <span className="invoice-meta-value">{field.value}</span>
            </div>
          ) : (
            <MetaCell key={field.label} label={field.label} value={field.value} />
          )
        )}
      </div>
    </div>
  );
};
